import React from "react";
import { Popup } from "semantic-ui-react";

// OCCROOMS-1: Availability center block extracted from DayCell.
// Renders the "<n> out of <X>" numerator/denominator block plus the
// localized label and the breakdown tooltip. Behavior is identical
// to the prior inline IIFE; only the location changed.
//
// availableMode=true  -> Available numerator (negative when overbooked; the OB allowance is a separate tooltip line)
// availableMode=false -> Occupied numerator (Sold)
// Denom is identical across both toggle states (FR-7/AC-7.2).
// HS-70658: the numbers are always rendered, even when every room is OOS
// (Denom 0), so the cell never disagrees with the tooltip / Daily Panel.
// isAllOos only adds the "All rooms OOS" line to the tooltip.
const AvailabilityCenter = React.memo(({
    availableMode,
    AvailableDisplay,
    Overbooking = 0,
    Sold,
    Denom,
    isAllOos,
    I,
    Oos,
    rawOoo,
    text,
}) => {
    const outOfText = text("OutOf");
    const outOfLabel = (outOfText && !outOfText.startsWith("[")) ? outOfText : "out of";
    const numerator = availableMode ? AvailableDisplay : Sold;
    const allOosTextRaw = text("AllRoomsOOS");
    const allOosLabel = (allOosTextRaw && !allOosTextRaw.startsWith("[")) ? allOosTextRaw : "All rooms OOS";
    const overbookingTextRaw = text("Overbooking");
    const overbookingLabel = (overbookingTextRaw && !overbookingTextRaw.startsWith("[")) ? overbookingTextRaw : "Overbooking";
    const ariaModeKey = availableMode ? "Available" : "Occupied";
    const ariaModeLegacyKey = availableMode ? "AvailableRooms" : "OccupancyRooms";
    const ariaModeFallback = availableMode ? "Available" : "Occupied";
    const ariaModeResolved = text(ariaModeKey);
    const ariaModeLegacy = text(ariaModeLegacyKey);
    const ariaModeLabel = (ariaModeResolved && !ariaModeResolved.startsWith("[")) ? ariaModeResolved
        : (ariaModeLegacy && !ariaModeLegacy.startsWith("[")) ? ariaModeLegacy
        : ariaModeFallback;
    const ariaLabel = `${ariaModeLabel}: ${numerator} ${outOfLabel} ${Denom}.`;
    const tooltipBreakdown = (
        <div className="availability-tooltip">
            <div>Total: {I}</div>
            <div>Sold: {Sold}</div>
            <div>OOO: {rawOoo ?? 0}</div>
            <div>OOS: {Oos}</div>
            <div>Available: {AvailableDisplay}</div>
            {/* Only a positive OB allowance; a hotel-level cap below the room type's Available is not "overbooking". */}
            {Overbooking > 0 && <div>{overbookingLabel}: {Overbooking}</div>}
            <div>Operational Inventory: {Denom}</div>
            {isAllOos && <div>{allOosLabel}</div>}
        </div>
    );
    return (
        <Popup
            trigger={
                <div className="day-cell-availability" aria-label={ariaLabel} tabIndex={0}>
                    <div className="availability-numbers">
                        <span className="avail-count">{numerator}</span>
                        <span className="avail-out-of">{outOfLabel}</span>
                        <span className="avail-total">{Denom}</span>
                    </div>
                    <span className="avail-label">
                        {(() => {
                            const key = availableMode ? "Available" : "Occupied";
                            const legacyKey = availableMode ? "AvailableRooms" : "OccupancyRooms";
                            const fallback = availableMode ? "Available" : "Occupied";
                            const resolved = text(key);
                            if (resolved && !resolved.startsWith("[")) return resolved;
                            const legacy = text(legacyKey);
                            if (legacy && !legacy.startsWith("[")) return legacy.toLowerCase();
                            return fallback;
                        })()}
                    </span>
                </div>
            }
            content={tooltipBreakdown}
        />
    );
});

export default AvailabilityCenter;
